import { cn } from '@/utils/cn';
import { TIconName } from '@/design-tokens';
import * as PhosphorIconsReact from '@phosphor-icons/react';

interface IconProps {
  className?: string;
  color?: string;
  name: TIconName;
  size?: number;
  type?: 'regular';
  weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
}

/**
 * Icon component using {@link https://phosphoricons.com/ | Phosphoricons}.
 */
export const Icon: React.FC<IconProps> = ({
  className,
  color,
  name,
  size = 24,
  type = 'regular',
  weight,
  ...props
}) => {
  /**
   * Construct the icon component name based on the provided name and type.
   */
  const iconName = name.charAt(0).toUpperCase() + name.slice(1) + 'Icon';

  /**
   * For small button icons, dynamically render the bold style of icons
   * unless a different weight is explicitly provided. Falls back to regular
   * weight by default.
   */
  const iconWeight = size <= 16 ? weight || 'bold' : weight || 'regular';

  /**
   * Render the Phosphor icon component if it exists
   */
  if (Object.prototype.hasOwnProperty.call(PhosphorIconsReact, iconName)) {
    const PhosphorIcon = (PhosphorIconsReact as any)[iconName] as React.ComponentType<any>;
    return (
      <PhosphorIcon
        className={cn('pointer-events-none', className)}
        color={color}
        data-name={iconName}
        data-weight={iconWeight}
        size={size}
        style={{ minWidth: size, minHeight: size }}
        weight={iconWeight}
        {...props}
      />
    );
  }

  return null;
};

Icon.displayName = 'Icon';

export default Icon;
